/**
 * Throws a DELETE request through the provided url with the data. It expects an API JSON response.
 *
 * @param url
 * @param data
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function apiDELETE(url, data) {
    let res = await DELETE(url, data);

    if (!!res && !!res.data && !!res.data.data) {
        return res.data.data;
    } else if (!!res && !!res.data) {
        return res.data;
    } else if (!!res) {
        return res;
    }

    return null;
}

/**
 * Throws a POST request through the provided url with the data. It expects an Api JSON response.
 *
 * @param url
 * @param data
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function apiPOST(url, data) {
    let res = await POST(url, data);

    if (!!res && !!res.data && !!res.data.data) {
        return res.data.data;
    } else if (!!res && !!res.data) {
        return res.data;
    } else if (!!res) {
        return res;
    }

    return null;
}

/**
 * Throws a PUT request through the provided url with the data. It expects an Api JSON response.
 *
 * @param url
 * @param data
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function apiPUT(url, data) {
    let res = await PUT(url, data);

    if (!!res && !!res.data && !!res.data.data) {
        return res.data.data;
    } else if (!!res && !!res.data) {
        return res.data;
    } else if (!!res) {
        return res;
    }

    return null;
}

/**
 * Throws a GET request through the provided url with the data. It expects an API JSON response.
 *
 * @param url
 * @param data
 * @returns {Promise<null|T|AxiosResponse<*>|*>}
 */
export async function apiGET(url, data = null) {
    let res = await GET(url, data);

    // Careful, if pagination is provided, here it is being omitted

    if (!!res && !!res.data && !!res.data.data) {
        return res.data.data;
    } else if (!!res && !!res.data) {
        return res.data;
    } else if (!!res) {
        return res;
    }

    return null;
}

/**
 * Throws a GET request through the provided url with the data. It expects an API JSON response with pagination.
 *
 * @param url
 * @param data
 . * @param cancelToken
 * @returns {Promise<null|{data}|T|AxiosResponse<*>|T>}
 */
export async function apiGETPaginated(url, data = null, cancelToken = null) {
    let res = await GET(url, data, cancelToken);

    if (!!res && !!res.data && !!res.data.data) {
        return res.data;
    } else if (!!res && !!res.data) {
        return res.data;
    } else if (!!res) {
        return res;
    }

    return null;
}

/**
 * Throws an axios POST request, to the provided url with the provided data and including cancel token if provided.
 *
 * @param url Just the url.
 * @param data Must be an object, for example: { email: 'ejemplo@gmail.com' }.
 * @param cancelToken Must be a variable and will be used to cancel previous request. Pass the same cancelToken variable
 * to a group of requests you want that one cancels each the other.
 * @returns {Promise<AxiosResponse<any>>}
 * @constructor
 */
export let POST = async (url, data, cancelToken = null) => {
    return await request(url, 'post', data, cancelToken);
}

/**
 * Throws an axios PUT request, to the provided url with the provided data and including cancel token if provided.
 *
 * @param url Just the url.
 * @param data Must be an object, for example: { email: 'ejemplo@gmail.com' }.
 * @param cancelToken Must be a variable and will be used to cancel previous request. Pass the same cancelToken variable
 * to a group of requests you want that one cancels each the other.
 * @returns {Promise<AxiosResponse<any>>}
 * @constructor
 */
export let PUT = async (url, data, cancelToken = null) => {
    return await request(url, 'put', data, cancelToken);
}

/**
 * Throws an axios DELETE request, to the provided url with the provided data and including cancel token if provided.
 *
 * @param url Just the url.
 * @param cancelToken Must be a variable and will be used to cancel previous request. Pass the same cancelToken variable
 * to a group of requests you want that one cancels each the other.
 * @returns {Promise<AxiosResponse<any>>}
 * @constructor
 */
export let DELETE = async (url, cancelToken = null) => {
    return await request(url, 'delete', null, cancelToken);
}


/**
 * Throws an axios GET request, to the provided url including cancel token if provided.
 *
 * @param url Just the url.
 * @param cancelToken Must be a variable and will be used to cancel previous request. Pass the same cancelToken variable
 * to a group of requests you want that one cancels each the other.
 * @param params Must be an object, for example: { email: 'ejemplo@gmail.com' }.
 * @returns {Promise<AxiosResponse<any>>}
 * @constructor
 */
export let GET = async (url, params = null, cancelToken = null) => {
    return await request(url, 'get', params, cancelToken);
}

// TODO: Enhance this to work as expected: a cancelToken is generated for each 'url' request
let request = async (url, method = 'get', data = null, cancelToken = null) => {
    // TODO: Please, refactor this with a dynamic config json, but I don't want to touch it because it breaks
    switch (method) {
        case "get":
            if (!!cancelToken) {
                return await axios.get(url, {
                    cancelToken: cancelToken,
                    params: data
                });
            } else {
                return await axios.get(url, {
                    params: data
                });
            }
        case "post":
            if (!!cancelToken) {
                return await axios.post(url, data, {
                    cancelToken: cancelToken
                });
            } else {
                return await axios.post(url, data);
            }
        case "put":
            if (!!cancelToken) {
                return await axios.put(url, data, {
                    cancelToken: cancelToken
                });
            } else {
                return await axios.put(url, data);
            }
        case "delete":
            if (!!cancelToken) {
                return await axios.delete(url, {
                    cancelToken: cancelToken
                });
            } else {
                return await axios.delete(url);
            }
    }
}

/**
 * To call a function after the provided milliseconds in timeout. The timer is reset when it is called again and it didn't fire.
 * (!) The 'timer' variable must be assigned to the local 'timer' in where this 'withTimeout' function is used. If I use
 * this in a .vue component, I declare a 'let timer = null' in that .vue component, then I use:
 * timer = withTimeout(function() { console.log('hello'); }, timer, 250).
 * If that timer is used again in another 'withTimeout' call, it will restart (so if no timeout passed, it won't fire
 * the first call).
 * @param theFunction
 * @param timer
 * @param timeout
 * @returns {number}
 */
export let withTimeout = (theFunction, timer, timeout = 1000) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
        theFunction();
    }, timeout);

    return timer;
}
